/**
 * Single source of truth for Caja pricing.
 *
 * Sync this file with `supabase/functions/_shared/pricing.ts`.
 * Both the frontend (PaywallBanner, CajaDashboardPage, AdminPage) and the
 * Edge Functions (create-mp-preference, mp-webhook) read plan metadata
 * from the same shape so prices never diverge between client and server.
 *
 * USD is the canonical price; COP is what MercadoPago charges in Colombia.
 * Update both fields when prices change.
 */

export const USD_TO_COP = 4200

/**
 * Duración del trial gratuito (en días) para nuevos usuarios Caja.
 * Se cuenta desde profiles.created_at hasta que el usuario activa
 * suscripción o expira el período.
 */
export const TRIAL_DAYS = 14

/**
 * Devuelve el estado del trial dado un ISO string de cuándo empezó.
 * Útil para countdown banners + soft urgency.
 *   {
 *     daysElapsed:    0..14 (cuántos días lleva)
 *     daysRemaining:  14..0 (cuántos faltan; 0 = expirado)
 *     dayN:           1..14 etiqueta visible "Día N de 14"
 *     pctElapsed:     0..100
 *     isExpired:      boolean
 *     urgency:        'fresh' | 'normal' | 'soon' | 'critical' | 'expired'
 *   }
 */
export function getTrialStatus(startISO) {
  if (!startISO) return null
  const start = new Date(startISO).getTime()
  const now   = Date.now()
  const dayMs = 24 * 60 * 60 * 1000
  const daysElapsed   = Math.max(0, Math.floor((now - start) / dayMs))
  const daysRemaining = Math.max(0, TRIAL_DAYS - daysElapsed)
  const dayN          = Math.min(TRIAL_DAYS, daysElapsed + 1)
  const pctElapsed    = Math.min(100, Math.round((daysElapsed / TRIAL_DAYS) * 100))
  const isExpired     = daysRemaining === 0
  const urgency =
    isExpired              ? 'expired'  :
    daysRemaining <= 2     ? 'critical' :
    daysRemaining <= 5     ? 'soon'     :
    daysRemaining <= 10    ? 'normal'   :
                             'fresh'
  return { daysElapsed, daysRemaining, dayN, pctElapsed, isExpired, urgency }
}

export const PLANS = {
  monthly: {
    id: 'monthly',
    kind: 'subscription',
    name: 'Plan Mensual',
    priceUSD: 50,
    priceCOP: 210000,
    durationMonths: 1,
    description: 'Acceso completo a Estrategia en una Caja por 1 mes.',
    statementDescriptor: 'NVC CAJA MES',
    badge: null,
  },
  annual: {
    id: 'annual',
    kind: 'subscription',
    name: 'Plan Anual',
    priceUSD: 400,
    priceCOP: 1680000,
    durationMonths: 12,
    description: 'Acceso completo por 12 meses. Ahorras 33% vs. el plan mensual.',
    statementDescriptor: 'NVC CAJA ANUAL',
    badge: 'Recomendado',
    savings: '33%',
  },
  session_1: {
    id: 'session_1',
    kind: 'sessions',
    name: '1 sesión con consultor',
    priceUSD: 150,
    priceCOP: 630000,
    sessions: 1,
    description: '1 hora de revisión con un consultor NVC.',
    statementDescriptor: 'NVC SESION',
  },
  pack_3: {
    id: 'pack_3',
    kind: 'sessions',
    name: 'Pack 3 sesiones',
    priceUSD: 400,
    priceCOP: 1680000,
    sessions: 3,
    description: '3 horas con consultor NVC (USD $133/sesión).',
    statementDescriptor: 'NVC PACK 3',
  },
  pack_5: {
    id: 'pack_5',
    kind: 'sessions',
    name: 'Pack 5 sesiones',
    priceUSD: 600,
    priceCOP: 2520000,
    sessions: 5,
    description: '5 horas con consultor NVC (USD $120/sesión).',
    statementDescriptor: 'NVC PACK 5',
    badge: 'Más popular',
  },
  pack_10: {
    id: 'pack_10',
    kind: 'sessions',
    name: 'Pack 10 sesiones',
    priceUSD: 1200,
    priceCOP: 5040000,
    sessions: 10,
    description: '10 horas con consultor NVC (USD $120/sesión).',
    statementDescriptor: 'NVC PACK 10',
  },
}

export const SUBSCRIPTION_PLAN_IDS = ['monthly', 'annual']
export const SESSION_PLAN_IDS = ['session_1', 'pack_3', 'pack_5', 'pack_10']

export const SUBSCRIPTION_FEATURES = [
  'Las 7 etapas completas de planificación estratégica',
  'Guías y tareas sugeridas para cada sección',
  'Agente de IA con contexto de tu proyecto',
  'Resultados exportables por sección',
  'Plan estratégico final descargable en Word y PDF',
]

export const SESSION_FEATURES = [
  'Revisión 1 a 1 con un consultor NVC',
  'Cada sesión dura 1 hora vía videollamada',
  'Las sesiones no caducan: úsalas cuando las necesites',
  'Agendamiento online después del pago',
]

export function getPlan(planId) {
  return PLANS[planId] ?? null
}

export function formatUSD(amount) {
  return `USD $${amount.toLocaleString('en-US')}`
}

export function formatCOP(amount) {
  return `COP $${amount.toLocaleString('es-CO')}`
}
